/**
 * TemplateSetupWizard
 *
 * A guided, plain-language wizard for creating CycleTemplates.
 * Templates are initialization blueprints applied once at cycle creation —
 * they seed criteria, participants, and groups.
 *
 * Step 1 asks what kind of items the cycle evaluates and auto-suggests
 * relevant criteria, preventing blank-slate paralysis.
 *
 * Accessible at: /prioritization/settings/templates/guided
 * Entry point: "Guided setup" button on the Templates settings page.
 *
 * Canon: "Workflow is always live. Templates are applied once."
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Loader2,
  Sparkles,
  Layers,
  Users,
  FolderKanban,
  Plus,
  X,
  Cpu,
  ShieldAlert,
  Lightbulb,
  Package,
  BarChart3,
  Wrench,
  LayoutTemplate,
} from 'lucide-react';
import { useCycleTemplateStore } from '../../../stores/prioritization/cycleTemplateStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useGroupsStore } from '../../../stores/prioritization/groupsStore';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { TemplateAttributePreset, TemplateRangePreset } from '../../../types/prioritization/cycleTemplate';
import {
  wizardBtnPrimary,
  wizardBtnSecondary,
  wizardCard,
  wizardCardPad,
  wizardInput,
  wizardSectionTitle,
  wizardSectionDesc,
  wizardShell,
  wizardHeaderBar,
} from '../../../components/prioritization/cycle-setup/setupWizardTheme';

// ─────────────────────────────────────────────────────────────────────────────
// Intent presets — criteria suggestions by cycle type
// ─────────────────────────────────────────────────────────────────────────────

type ItemIntent =
  | 'product'
  | 'project'
  | 'idea'
  | 'risk'
  | 'support'
  | 'research'
  | 'custom';

type SuggestedScale = 'fibonacci' | 'tshirt' | 'lmh' | 'number_1_5' | 'number_1_10';

interface SuggestedCriterion {
  key: string;
  label: string;
  scale: SuggestedScale;
  reason: string;
}

const INTENT_SUGGESTIONS: Record<Exclude<ItemIntent, 'custom'>, SuggestedCriterion[]> = {
  product: [
    { key: 'impact', label: 'Customer impact', scale: 'fibonacci', reason: 'How much does this move the needle for customers?' },
    { key: 'effort', label: 'Effort', scale: 'tshirt', reason: 'How much work is required to build this?' },
    { key: 'strategic_fit', label: 'Strategic fit', scale: 'number_1_5', reason: 'How well does this align with current strategy?' },
    { key: 'confidence', label: 'Confidence', scale: 'number_1_5', reason: 'How sure are we about estimates and impact?' },
  ],
  project: [
    { key: 'value', label: 'Business value', scale: 'fibonacci', reason: 'What is the expected return or value delivered?' },
    { key: 'complexity', label: 'Complexity', scale: 'tshirt', reason: 'How complex is this project to execute?' },
    { key: 'risk', label: 'Risk', scale: 'lmh', reason: 'How much risk does this project carry?' },
    { key: 'urgency', label: 'Urgency', scale: 'lmh', reason: 'How time-sensitive is this?' },
  ],
  idea: [
    { key: 'novelty', label: 'Novelty', scale: 'number_1_5', reason: 'How fresh or differentiated is this idea?' },
    { key: 'market_potential', label: 'Market potential', scale: 'number_1_5', reason: 'How big is the addressable opportunity?' },
    { key: 'feasibility', label: 'Feasibility', scale: 'tshirt', reason: 'How viable is this to actually build or test?' },
    { key: 'alignment', label: 'Mission alignment', scale: 'lmh', reason: 'Does this fit our current focus?' },
  ],
  risk: [
    { key: 'likelihood', label: 'Likelihood', scale: 'lmh', reason: 'How probable is this risk occurring?' },
    { key: 'severity', label: 'Severity', scale: 'lmh', reason: 'How bad would the impact be if it occurs?' },
    { key: 'detectability', label: 'Detectability', scale: 'number_1_5', reason: 'How easy is it to detect before it causes harm?' },
    { key: 'mitigation_effort', label: 'Mitigation effort', scale: 'tshirt', reason: 'How much work is needed to reduce this risk?' },
  ],
  support: [
    { key: 'urgency', label: 'Urgency', scale: 'lmh', reason: 'How time-sensitive is this for the customer?' },
    { key: 'customer_impact', label: 'Customer impact', scale: 'fibonacci', reason: 'How many customers are affected or how severely?' },
    { key: 'effort', label: 'Resolution effort', scale: 'tshirt', reason: 'How much engineering time is needed?' },
    { key: 'recurrence', label: 'Recurrence', scale: 'number_1_5', reason: 'How often does this type of issue come up?' },
  ],
  research: [
    { key: 'impact', label: 'Potential impact', scale: 'fibonacci', reason: 'How significant could the findings be?' },
    { key: 'confidence', label: 'Confidence in approach', scale: 'number_1_5', reason: 'How sure are we this research will yield useful results?' },
    { key: 'timeline', label: 'Timeline fit', scale: 'tshirt', reason: 'Does the effort fit within available time?' },
    { key: 'strategic_relevance', label: 'Strategic relevance', scale: 'lmh', reason: 'How directly does this inform current decisions?' },
  ],
};

const SCALE_LABELS: Record<SuggestedScale, { label: string; preview: string }> = {
  fibonacci: { label: 'Fibonacci', preview: '1, 2, 3, 5, 8, 13, 21' },
  tshirt: { label: 'T-shirt sizes', preview: 'XS, S, M, L, XL, XXL' },
  lmh: { label: 'Low / Medium / High', preview: 'Low, Medium, High' },
  number_1_5: { label: 'Numbers 1–5', preview: '1, 2, 3, 4, 5' },
  number_1_10: { label: 'Numbers 1–10', preview: '1 – 10' },
};

function scaleToRange(scale: SuggestedScale): TemplateRangePreset {
  switch (scale) {
    case 'fibonacci': return { type: 'fibonacci', options: [1, 2, 3, 5, 8, 13, 21] };
    case 'tshirt': return { type: 'tshirt', options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] };
    case 'lmh': return { type: 'select', options: ['Low', 'Medium', 'High'] };
    case 'number_1_5': return { type: 'number', min: 1, max: 5, step: 1 };
    case 'number_1_10': return { type: 'number', min: 1, max: 10, step: 1 };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Wizard state
// ─────────────────────────────────────────────────────────────────────────────

interface DraftCriterion {
  key: string;
  label: string;
  scale: SuggestedScale;
  required: boolean;
  source: 'suggested' | 'workspace' | 'custom';
}

interface WizardState {
  name: string;
  description: string;
  intent: ItemIntent | null;
  criteria: DraftCriterion[];
  participantIds: string[];
  groupIds: string[];
}

const INITIAL: WizardState = {
  name: '',
  description: '',
  intent: null,
  criteria: [],
  participantIds: [],
  groupIds: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI helpers
// ─────────────────────────────────────────────────────────────────────────────

function OptionCard<T extends string>({
  value,
  current,
  icon: Icon,
  title,
  subtitle,
  onClick,
}: {
  value: T;
  current: T | null;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  onClick: (v: T) => void;
}) {
  const selected = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={[
        'w-full text-left  border-2 p-4 transition-all focus:outline-none focus:ring-2 focus:ring-primary-400',
        selected
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30 dark:border-primary-500'
          : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-primary-300 hover:bg-primary-50/40 dark:hover:border-primary-700 dark:hover:bg-primary-950/15',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <div className={[
          'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center ',
          selected
            ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-300'
            : 'bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-ter)] dark:text-[var(--text-sec)]',
        ].join(' ')}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={['text-sm font-semibold', selected ? 'text-primary-800 dark:text-primary-200' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'].join(' ')}>{title}</p>
          <p className={['mt-0.5 text-xs leading-relaxed', selected ? 'text-primary-600 dark:text-primary-300' : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]'].join(' ')}>{subtitle}</p>
        </div>
        {selected && <CheckCircle2 size={18} className="flex-shrink-0 text-primary-500 dark:text-primary-400" />}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Name + intent
// ─────────────────────────────────────────────────────────────────────────────

function StepIntent({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  function selectIntent(intent: ItemIntent) {
    const suggestions = intent !== 'custom' ? INTENT_SUGGESTIONS[intent] : [];
    // Pre-populate criteria with suggested ones (preserve any already added)
    const existingKeys = new Set(state.criteria.map(c => c.key));
    const newCriteria: DraftCriterion[] = [
      ...state.criteria.filter(c => c.source !== 'suggested'), // keep non-suggested
      ...suggestions
        .filter(s => !existingKeys.has(s.key))
        .map(s => ({ key: s.key, label: s.label, scale: s.scale, required: true, source: 'suggested' as const })),
    ];
    update({ intent, criteria: newCriteria });
  }

  return (
    <div className="space-y-6">
      <div>
        <label className={`block mb-1 ${wizardSectionTitle}`}>What should we call this template?</label>
        <p className={wizardSectionDesc}>This name appears in the template selector when creating a cycle.</p>
        <input
          className={`mt-2 ${wizardInput}`}
          placeholder="e.g. Product Feature Prioritization"
          value={state.name}
          onChange={e => update({ name: e.target.value })}
          autoFocus
        />
      </div>

      <div>
        <p className={`mb-2 ${wizardSectionTitle}`}>What kind of items will this cycle evaluate?</p>
        <p className={`mb-3 ${wizardSectionDesc}`}>
          We'll suggest the most relevant criteria to start from. You can adjust everything on the next screen.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          <OptionCard value="product" current={state.intent} icon={Cpu} onClick={selectIntent}
            title="Product features or requests" subtitle="Impact, Effort, Strategic fit — scored by a product team" />
          <OptionCard value="project" current={state.intent} icon={FolderKanban} onClick={selectIntent}
            title="Projects or initiatives" subtitle="Business value, Complexity, Risk — for portfolio decisions" />
          <OptionCard value="idea" current={state.intent} icon={Lightbulb} onClick={selectIntent}
            title="Ideas or concepts" subtitle="Novelty, Market potential, Feasibility — for early-stage evaluation" />
          <OptionCard value="risk" current={state.intent} icon={ShieldAlert} onClick={selectIntent}
            title="Risks" subtitle="Likelihood, Severity, Detectability — for risk registers" />
          <OptionCard value="support" current={state.intent} icon={Package} onClick={selectIntent}
            title="Support or operational issues" subtitle="Urgency, Customer impact, Resolution effort" />
          <OptionCard value="research" current={state.intent} icon={BarChart3} onClick={selectIntent}
            title="Research items or experiments" subtitle="Potential impact, Confidence, Timeline fit" />
          <OptionCard value="custom" current={state.intent} icon={Wrench} onClick={selectIntent}
            title="Something else — I'll pick my own" subtitle="Start blank and choose criteria manually" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Criteria selection
// ─────────────────────────────────────────────────────────────────────────────

function StepCriteria({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const attributes = useAttributesStore(s => s.attributes) ?? [];
  const [showCustom, setShowCustom] = useState(false);
  const [customLabel, setCustomLabel] = useState('');

  const usedKeys = new Set(state.criteria.map(c => c.key));
  const suggestions = state.intent && state.intent !== 'custom' ? INTENT_SUGGESTIONS[state.intent] : [];
  const workspaceAvailable = attributes.filter(a =>
    !usedKeys.has(a.key) && (!a.isCustomField || a.usable_as_criterion === true)
  );

  function toggle(key: string, label: string, scale: SuggestedScale, source: DraftCriterion['source']) {
    if (usedKeys.has(key)) {
      update({ criteria: state.criteria.filter(c => c.key !== key) });
    } else {
      update({ criteria: [...state.criteria, { key, label, scale, required: true, source }] });
    }
  }

  function addCustom() {
    const label = customLabel.trim();
    if (!label) return;
    const key = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (usedKeys.has(key)) return;
    update({ criteria: [...state.criteria, { key, label, scale: 'fibonacci', required: true, source: 'custom' }] });
    setCustomLabel('');
    setShowCustom(false);
  }

  return (
    <div className="space-y-6">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>Which criteria will you use to evaluate items?</p>
        <p className={wizardSectionDesc}>Select everything that's relevant. You'll configure the scoring scale for each on the next screen.</p>
      </div>

      {/* Suggested criteria */}
      {suggestions.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-2">
            Suggested for {state.intent === 'product' ? 'product features' : state.intent === 'project' ? 'projects' : state.intent === 'idea' ? 'ideas' : state.intent === 'risk' ? 'risks' : state.intent === 'support' ? 'support items' : 'research'}
          </p>
          <div className="space-y-2">
            {suggestions.map(s => {
              const selected = usedKeys.has(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggle(s.key, s.label, s.scale, 'suggested')}
                  className={[
                    'w-full flex items-center gap-3  border-2 px-4 py-3 text-left transition-all',
                    selected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30 dark:border-primary-500'
                      : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-primary-300 dark:hover:border-primary-700',
                  ].join(' ')}
                >
                  <div className={[
                    'h-5 w-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors',
                    selected ? 'border-primary-500 bg-primary-500' : 'border-[var(--rule)] dark:border-[var(--rule-bright)]',
                  ].join(' ')}>
                    {selected && <Check size={12} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={['text-sm font-semibold', selected ? 'text-primary-800 dark:text-primary-200' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'].join(' ')}>{s.label}</p>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">{s.reason}</p>
                  </div>
                  <span className={['text-xs px-2 py-0.5 rounded-full border', selected ? 'border-primary-200 text-primary-600 bg-primary-50 dark:bg-primary-950/40 dark:border-primary-700 dark:text-primary-300' : 'border-[var(--rule)] text-[var(--text-ter)] dark:border-[var(--rule)] dark:text-[var(--text-ter)]'].join(' ')}>
                    {SCALE_LABELS[s.scale].label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Workspace attributes */}
      {workspaceAvailable.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-2">
            From your workspace
          </p>
          <div className="flex flex-wrap gap-2">
            {workspaceAvailable.map(a => (
              <button
                key={a.key}
                type="button"
                onClick={() => toggle(a.key, a.label, 'fibonacci', 'workspace')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:border-primary-400 hover:bg-primary-50 dark:hover:border-primary-600 dark:hover:bg-primary-950/20 transition-colors"
              >
                <Plus size={13} className="text-[var(--text-ter)]" />
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom */}
      <div className="pt-1 border-t border-[var(--rule)] dark:border-[var(--rule)]">
        {!showCustom ? (
          <button type="button" onClick={() => setShowCustom(true)} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
            + Add a custom criterion not listed above
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              placeholder="e.g. Customer retention impact"
              value={customLabel}
              onChange={e => setCustomLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } if (e.key === 'Escape') setShowCustom(false); }}
              className={`flex-1 ${wizardInput}`}
            />
            <button type="button" disabled={!customLabel.trim()} onClick={addCustom} className={`${wizardBtnSecondary} disabled:opacity-40`}>
              <Plus size={14} /> Add
            </button>
            <button type="button" onClick={() => setShowCustom(false)} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]">
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Summary of selected */}
      {state.criteria.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {state.criteria.map(c => (
            <span key={c.key} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 dark:bg-primary-950/40 text-primary-800 dark:text-primary-300 text-xs font-medium border border-primary-200 dark:border-primary-800/60">
              {c.label}
              <button type="button" onClick={() => update({ criteria: state.criteria.filter(x => x.key !== c.key) })} className="text-primary-400 hover:text-[var(--coral)] ml-0.5">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 3 — Configure scales
// ─────────────────────────────────────────────────────────────────────────────

const ALL_SCALES: SuggestedScale[] = ['fibonacci', 'tshirt', 'lmh', 'number_1_5', 'number_1_10'];

function StepScales({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  function updateCriterion(key: string, patch: Partial<DraftCriterion>) {
    update({ criteria: state.criteria.map(c => c.key === key ? { ...c, ...patch } : c) });
  }

  if (state.criteria.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        <Layers size={32} className="mx-auto mb-2 opacity-40" />
        <p className="text-sm">No criteria selected — go back and add at least one.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>Configure scoring scales</p>
        <p className={wizardSectionDesc}>
          Set the scale and whether each criterion is required. You can leave "Required" off if teams should be able to skip it.
        </p>
      </div>

      <div className="space-y-3">
        {state.criteria.map(c => (
          <div key={c.key} className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{c.label}</p>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">key: {c.key}</p>
              </div>
              <button type="button" onClick={() => update({ criteria: state.criteria.filter(x => x.key !== c.key) })}
                className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors mt-0.5">
                <X size={14} />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-1">Scoring scale</label>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_SCALES.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => updateCriterion(c.key, { scale: s })}
                      className={[
                        'px-2.5 py-1  border text-xs font-medium transition-colors',
                        c.scale === s
                          ? 'border-primary-400 bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:border-primary-600 dark:text-primary-300'
                          : 'border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:border-primary-300',
                      ].join(' ')}
                    >
                      {SCALE_LABELS[s].label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
                  {SCALE_LABELS[c.scale].preview}
                </p>
              </div>

              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-1">Required?</label>
                <button
                  type="button"
                  onClick={() => updateCriterion(c.key, { required: !c.required })}
                  className={[
                    'relative h-5 w-9 rounded-full transition-colors focus:outline-none',
                    c.required ? 'bg-primary-500' : 'bg-[var(--rule-bright)] dark:bg-[var(--rule-bright)]',
                  ].join(' ')}
                >
                  <span className={['absolute top-0.5 h-4 w-4 rounded-full bg-[var(--bg-raised)] shadow transition-transform', c.required ? 'translate-x-4' : 'translate-x-0.5'].join(' ')} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 4 — Participants
// ─────────────────────────────────────────────────────────────────────────────

function StepParticipants({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const { users } = useSystemUsers();

  const selected = users.filter(u => state.participantIds.includes(u.id));
  const available = users.filter(u => !state.participantIds.includes(u.id));

  function toggle(id: string) {
    if (state.participantIds.includes(id)) {
      update({ participantIds: state.participantIds.filter(x => x !== id) });
    } else {
      update({ participantIds: [...state.participantIds, id] });
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>Default participants <span className="font-normal text-[var(--text-ter)] dark:text-[var(--text-ter)]">(optional)</span></p>
        <p className={wizardSectionDesc}>
          These people will automatically be added as participants to any cycle created from this template.
          Skip this if different cycles will have different participants.
        </p>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(u => (
            <span key={u.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-50 dark:bg-primary-950/30 text-primary-800 dark:text-primary-300 text-xs font-medium border border-primary-200 dark:border-primary-700/60">
              {u.name || u.email || u.id.slice(0, 8)}
              <button type="button" onClick={() => toggle(u.id)} className="text-primary-400 hover:text-[var(--coral)]">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {available.length > 0 ? (
        <div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-2">
            {selected.length === 0 ? 'Select participants to include by default:' : 'Add more:'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {available.map(u => (
              <button key={u.id} type="button" onClick={() => toggle(u.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:border-primary-400 hover:bg-primary-50 dark:hover:border-primary-600 dark:hover:bg-primary-950/20 transition-colors">
                <Plus size={13} className="text-[var(--text-ter)]" />
                {u.name || u.email || u.id.slice(0, 8)}
              </button>
            ))}
          </div>
        </div>
      ) : selected.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic">No workspace users found.</p>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 5 — Groups
// ─────────────────────────────────────────────────────────────────────────────

function StepGroups({ state, update }: { state: WizardState; update: (p: Partial<WizardState>) => void }) {
  const groups = useGroupsStore(s => s.groups) ?? [];

  const selected = groups.filter(g => state.groupIds.includes(g.id));
  const available = groups.filter(g => !state.groupIds.includes(g.id));

  function toggle(id: string) {
    if (state.groupIds.includes(id)) {
      update({ groupIds: state.groupIds.filter(x => x !== id) });
    } else {
      update({ groupIds: [...state.groupIds, id] });
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>Default groups <span className="font-normal text-[var(--text-ter)] dark:text-[var(--text-ter)]">(optional)</span></p>
        <p className={wizardSectionDesc}>
          These groups will be added to any cycle created from this template.
          Good for standing teams that always participate in this type of cycle.
        </p>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(g => (
            <span key={g.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] text-xs font-medium border border-[var(--slate)] dark:border-[var(--slate)]/60">
              {g.displayName || g.name || g.id.slice(0, 8)}
              <button type="button" onClick={() => toggle(g.id)} className="text-[var(--text-sec)] hover:text-[var(--coral)]">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {available.length > 0 ? (
        <div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-2">
            {selected.length === 0 ? 'Select groups to include by default:' : 'Add more:'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {available.map(g => (
              <button key={g.id} type="button" onClick={() => toggle(g.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] dark:hover:border-[var(--slate)] dark:hover:bg-[var(--slate)]/20 transition-colors">
                <Plus size={13} className="text-[var(--text-ter)]" />
                {g.displayName || g.name || g.id.slice(0, 8)}
              </button>
            ))}
          </div>
        </div>
      ) : selected.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic">No groups defined in this workspace.</p>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 6 — Review
// ─────────────────────────────────────────────────────────────────────────────

function StepReview({ state }: { state: WizardState }) {
  const { users } = useSystemUsers();
  const groups = useGroupsStore(s => s.groups) ?? [];

  return (
    <div className="space-y-4">
      <div className={wizardCardPad}>
        <div className="flex items-center gap-2 mb-3">
          <LayoutTemplate size={15} className="text-primary-500" />
          <p className={wizardSectionTitle}>
            Template: <span className="text-primary-700 dark:text-primary-300">{state.name || '(unnamed)'}</span>
          </p>
        </div>

        {state.criteria.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-1">Criteria</p>
            {state.criteria.map(c => (
              <div key={c.key} className="flex items-center justify-between py-2 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] font-medium">{c.label}</span>
                  {c.required && (
                    <span className="text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 px-1.5 py-0.5 rounded-full">required</span>
                  )}
                </div>
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{SCALE_LABELS[c.scale].preview}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic">No criteria — cycles will start blank.</p>
        )}

        {state.participantIds.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)]">
            <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-1.5">Default participants</p>
            <div className="flex flex-wrap gap-1">
              {state.participantIds.map(id => {
                const u = users.find(x => x.id === id);
                return (
                  <span key={id} className="text-xs px-2 py-0.5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]/50">
                    {u?.name || u?.email || id.slice(0, 8)}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {state.groupIds.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)]">
            <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-1.5">Default groups</p>
            <div className="flex flex-wrap gap-1">
              {state.groupIds.map(id => {
                const g = groups.find(x => x.id === id);
                return (
                  <span key={id} className="text-xs px-2 py-0.5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]/50">
                    {g?.displayName || g?.name || id.slice(0, 8)}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className={`${wizardCard} p-4`}>
        <div className="flex items-start gap-2 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
          <Layers size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            <strong>Applied once:</strong> When a cycle is created from this template, these criteria, participants, and groups are seeded in.
            After that the cycle owns them — changes to this template won't affect existing cycles.
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Steps config
// ─────────────────────────────────────────────────────────────────────────────

interface Step {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  isComplete: (s: WizardState) => boolean;
}

const STEPS: Step[] = [
  { id: 'intent', title: 'Name & purpose', subtitle: "What kind of items are you evaluating?", icon: Sparkles, isComplete: s => !!s.name.trim() && !!s.intent },
  { id: 'criteria', title: 'Choose criteria', subtitle: 'Which dimensions matter?', icon: Layers, isComplete: () => true },
  { id: 'scales', title: 'Scoring scales', subtitle: 'How will each criterion be scored?', icon: BarChart3, isComplete: () => true },
  { id: 'participants', title: 'Default participants', subtitle: 'Who participates by default?', icon: Users, isComplete: () => true },
  { id: 'groups', title: 'Default groups', subtitle: 'Which groups participate by default?', icon: FolderKanban, isComplete: () => true },
  { id: 'review', title: 'Review & create', subtitle: 'Confirm and create the template', icon: Check, isComplete: () => true },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main wizard
// ─────────────────────────────────────────────────────────────────────────────

export default function TemplateSetupWizard() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const createTemplate = useCycleTemplateStore(s => s.createTemplate);
  const { users } = useSystemUsers();
  const groups = useGroupsStore(s => s.groups) ?? [];
  const userId = useUserStore(s => s.userId) ?? '';

  function update(patch: Partial<WizardState>) {
    setState(prev => ({ ...prev, ...patch }));
  }

  const currentStep = STEPS[step];
  const canAdvance = currentStep.isComplete(state);
  const isLast = step === STEPS.length - 1;

  // Skip scales step if no criteria selected
  function advance() {
    let next = step + 1;
    if (STEPS[next]?.id === 'scales' && state.criteria.length === 0) next++;
    setStep(next);
  }

  function retreat() {
    let prev = step - 1;
    if (STEPS[prev]?.id === 'scales' && state.criteria.length === 0) prev--;
    setStep(Math.max(0, prev));
  }

  const defaultAttributes = useMemo((): TemplateAttributePreset[] =>
    state.criteria.map(c => ({
      attributeKey: c.key,
      required: c.required,
      defaultRange: scaleToRange(c.scale),
    })), [state.criteria]);

  const defaultParticipants = useMemo(() =>
    state.participantIds.map(id => {
      const u = users.find(x => x.id === id);
      return { userId: id, displayName: u?.name || u?.email || id };
    }), [state.participantIds, users]);

  const defaultGroups = useMemo(() =>
    state.groupIds.map(id => {
      const g = groups.find(x => x.id === id);
      return { groupId: id, displayName: g?.displayName || g?.name || id };
    }), [state.groupIds, groups]);

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      await createTemplate({
        name: state.name,
        description: state.description || undefined,
        defaultAttributes,
        defaultParticipants,
        defaultGroups,
        createdBy: userId,
      });
      navigate('/prioritization/settings/templates', { state: { created: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function renderStep() {
    switch (STEPS[step].id) {
      case 'intent': return <StepIntent state={state} update={update} />;
      case 'criteria': return <StepCriteria state={state} update={update} />;
      case 'scales': return <StepScales state={state} update={update} />;
      case 'participants': return <StepParticipants state={state} update={update} />;
      case 'groups': return <StepGroups state={state} update={update} />;
      case 'review': return <StepReview state={state} />;
      default: return null;
    }
  }

  return (
    <div className={wizardShell}>
      {/* Header */}
      <div className={`sticky top-0 z-20 ${wizardHeaderBar}`}>
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => step > 0 ? retreat() : navigate('/prioritization/settings/templates')}
            className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] transition-colors"
          >
            <ArrowLeft size={16} />
            {step > 0 ? 'Back' : 'Cancel'}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={s.id} className={['h-1.5 rounded-full transition-all', i < step ? 'w-4 bg-primary-400' : i === step ? 'w-6 bg-primary-600' : 'w-1.5 bg-[var(--hover-tint)] dark:bg-[var(--rule)]'].join(' ')} />
            ))}
          </div>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{step + 1} / {STEPS.length}</span>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            {(() => { const Icon = currentStep.icon; return <Icon size={18} className="text-primary-500" />; })()}
            <h1 className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{currentStep.title}</h1>
          </div>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{currentStep.subtitle}</p>
        </div>

        {renderStep()}

        {error && (
          <div className="mt-4 border border-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 dark:border-[var(--coral)]/50 p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
            {error}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={() => step > 0 ? retreat() : navigate('/prioritization/settings/templates')}
            className={wizardBtnSecondary}
          >
            <ArrowLeft size={16} />
            {step > 0 ? 'Back' : 'Cancel'}
          </button>

          {isLast ? (
            <button onClick={handleCreate} disabled={saving || !state.name.trim()} className={`${wizardBtnPrimary} min-w-[140px]`}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {saving ? 'Creating…' : 'Create template'}
            </button>
          ) : (
            <button onClick={advance} disabled={!canAdvance} className={`${wizardBtnPrimary} disabled:opacity-40`}>
              Continue <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
